import { useState } from 'react';
import Image from 'next/image';

import { GlobeOutline, ImageOutline } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';

import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';
import ImageViewerModal from '../../components/ImageViewerModal';

interface DocumentationsProps {
  images: string[];
  links: string[];
  solutionWinSalesforceLinks: string[];
}

const Documentations = ({ images, links, solutionWinSalesforceLinks }: DocumentationsProps) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const mainImage = images[0];
  const additionalImagesCount = images.length - 1;

  return (
    <Stack className="w-full xs:min-w-full xs:p-12 xs:mt-12" direction="col" gapY={32}>
      <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
        {/* Image section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" direction="row" gapX={12} justifyContent="between">
            <Stack alignItems="center" direction="row" gapX={8}>
              <ImageOutline fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">
                Image{additionalImagesCount > 0 ? ` +${additionalImagesCount}` : ''}
              </Typography>
            </Stack>
            {mainImage && (
              <Typography
                className="cursor-pointer text-text-link hover:underline"
                variant="body-short-02"
                onClick={() => setIsViewerOpen(true)}>
                View all
              </Typography>
            )}
          </Stack>
          {mainImage ? (
            <div className="relative w-full h-[200px] rounded-12 bg-bg-secondary">
              <Image
                fill
                alt="Solution Win"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                src={mainImage}
                style={{ objectFit: 'contain', backgroundColor: 'var(--bg-secondary)' }}
              />
            </div>
          ) : (
            <EmptyState />
          )}
        </Stack>

        {/* Links section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" direction="row" gapX={12} justifyContent="between">
            <Stack alignItems="center" gapX={8}>
              <GlobeOutline fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">Links</Typography>
            </Stack>
          </Stack>
          <Stack className="flex-1 mt-24 overflow-hidden" direction="col">
            {links.length > 0 ? (
              links.map((link, index) => (
                <Stack
                  key={index}
                  className={`cursor-pointer group pb-12 mb-12 w-full ${
                    index !== links.length - 1 ? 'border-b border-border' : ''
                  }`}
                  onClick={() => window.open(link, '_blank')}>
                  <Typography
                    className="text-link-external group-hover:text-link-external-hover"
                    variant="body-long-01">
                    {link}
                  </Typography>
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Salesforce links section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" direction="row" gapX={12} justifyContent="between">
            <Stack alignItems="center" gapX={8}>
              <GlobeOutline fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">Salesforce Opportunities</Typography>
            </Stack>
          </Stack>
          <Stack className="flex-1 mt-24 overflow-hidden" direction="col">
            {solutionWinSalesforceLinks.length > 0 ? (
              solutionWinSalesforceLinks.map((link, index) => (
                <Stack
                  key={index}
                  className={`cursor-pointer group pb-12 mb-12 w-full ${
                    index !== links.length - 1 ? 'border-b border-border' : ''
                  }`}
                  onClick={() => window.open(link, '_blank')}>
                  <Typography
                    className="text-link-external group-hover:text-link-external-hover"
                    variant="body-long-01">
                    {link}
                  </Typography>
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>
      </Stack>

      {mainImage && (
        <ImageViewerModal
          images={images}
          isOpen={isViewerOpen}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
    </Stack>
  );
};

export default Documentations;
